/**
 * Acceso a datos de Existencias (productos + inventario por sucursal).
 *
 * POR QUÉ EXISTE: la página traía `products` e `inventory` SIN paginar. PostgREST corta en
 * 1000 filas (max-rows), así que:
 *  - con >1000 productos activos, la lista quedaba incompleta, y
 *  - `inventory` tiene UNA FILA POR (producto × sucursal), así que se pasa de 1000 con
 *    catálogos medianos (p. ej. 300 productos × 5 locales = 1500) → los totales de stock
 *    se truncaban y quedaban MAL (no vacíos: mal), en silencio y sin error.
 *
 * Acá se pagina con `.range()` hasta traer todo, y los `.in(productIds)` se trocean para no
 * generar una URL gigante (riesgo de 414). El orden estable (`.order('id')`) es obligatorio:
 * sin él, dos filas empatadas pueden saltar de página y perderse.
 *
 * NOTA de escala: esto es CORRECTO a cualquier tamaño, pero sigue trayendo el catálogo al
 * cliente. La agregación de stock en SQL (RPC) es el siguiente paso para catálogos grandes.
 */
import { supabase } from '@/lib/supabase';

/** Filas por request: el techo duro de PostgREST. */
const PAGE_SIZE = 1000;
/** IDs por request en un `.in(...)`: acota el largo de la URL. */
const ID_CHUNK = 300;

export interface StockProductRow {
  id: string;
  name: string;
  category: string | null;
  image_url: string | null;
  stock: number | null;
  slug: string | null;
}

export interface InventoryQuantityRow {
  product_id: string;
  quantity: number;
}

/** TODOS los productos activos del tenant, paginando (antes truncaba en 1000). */
export async function fetchAllActiveProductsForStock(tenantId: string): Promise<StockProductRow[]> {
  const all: StockProductRow[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('products')
      .select('id, name, category, image_url, stock, slug')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .order('category')
      .order('name')
      .order('id') // desempate estable entre páginas
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const rows = (data ?? []) as StockProductRow[];
    all.push(...rows);
    if (rows.length < PAGE_SIZE) break;
  }
  return all;
}

/**
 * TODAS las filas de inventario de esos productos (opcionalmente acotado a sucursales),
 * troceando los ids y paginando cada trozo. Lanza si falla: quien llama NO debe caer a un
 * fallback silencioso (mostrar `product.stock` como si fuera el real es peor que un error).
 */
export async function fetchInventoryQuantities(
  productIds: string[],
  branchIds: string[] | null,
): Promise<InventoryQuantityRow[]> {
  const rows: InventoryQuantityRow[] = [];
  if (productIds.length === 0) return rows;
  if (branchIds && branchIds.length === 0) return rows;

  for (let i = 0; i < productIds.length; i += ID_CHUNK) {
    const ids = productIds.slice(i, i + ID_CHUNK);
    for (let from = 0; ; from += PAGE_SIZE) {
      let q = supabase.from('inventory').select('product_id, quantity').in('product_id', ids);
      if (branchIds && branchIds.length > 0) q = q.in('branch_id', branchIds);
      const { data, error } = await q.order('id').range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      const page = (data ?? []) as InventoryQuantityRow[];
      rows.push(...page);
      if (page.length < PAGE_SIZE) break;
    }
  }
  return rows;
}

/** Suma de existencias por producto. Pura: testeable sin tocar la BD. */
export function sumQuantitiesByProduct(rows: InventoryQuantityRow[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const r of rows) {
    totals[r.product_id] = (totals[r.product_id] ?? 0) + (Number(r.quantity) || 0);
  }
  return totals;
}
